import { startOfDay } from "date-fns"

export interface LodgeStayFormValue {
  startDate?: Date
  endDate?: Date
}

export interface LodgeStayValidationIssue {
  path: (string | number)[]
  message: string
}

export function getLodgeStayValidationIssues(values: {
  guestType: "lodge" | "camping" | "mixed"
  arrivalDate?: Date
  departureDate?: Date
  lodgeStays: LodgeStayFormValue[]
}): LodgeStayValidationIssue[] {
  if (values.guestType !== "mixed") {
    return values.lodgeStays.length === 0
      ? []
      : [
          {
            path: ["lodgeStays"],
            message: "Lodge stays are only allowed for mixed bookings",
          },
        ]
  }

  if (values.lodgeStays.length === 0) {
    return [{ path: ["lodgeStays"], message: "Add at least one lodge stay" }]
  }

  const issues: LodgeStayValidationIssue[] = []
  for (const [index, stay] of values.lodgeStays.entries()) {
    if (!stay.startDate) {
      issues.push({
        path: ["lodgeStays", index, "startDate"],
        message: "Lodge start date is required",
      })
    }

    if (!stay.endDate) {
      issues.push({
        path: ["lodgeStays", index, "endDate"],
        message: "Lodge end date is required",
      })
    }
  }

  const complete = values.lodgeStays
    .map((stay, index) => ({ ...stay, index }))
    .filter(
      (
        stay,
      ): stay is LodgeStayFormValue & {
        index: number
        startDate: Date
        endDate: Date
      } => stay.startDate !== undefined && stay.endDate !== undefined,
    )

  for (const stay of complete) {
    const start = startOfDay(stay.startDate)
    const end = startOfDay(stay.endDate)

    if (end < start) {
      issues.push({
        path: ["lodgeStays", stay.index, "endDate"],
        message: "Lodge end date must be on or after start date",
      })
    }

    if (
      values.arrivalDate &&
      values.departureDate &&
      (start < startOfDay(values.arrivalDate) ||
        end > startOfDay(values.departureDate))
    ) {
      issues.push({
        path: ["lodgeStays", stay.index, "startDate"],
        message: "Lodge stays must be within the booking dates",
      })
    }
  }

  const ordered = [...complete].sort(
    (left, right) => left.startDate.getTime() - right.startDate.getTime(),
  )
  for (let index = 1; index < ordered.length; index += 1) {
    if (
      startOfDay(ordered[index].startDate) <=
      startOfDay(ordered[index - 1].endDate)
    ) {
      issues.push({
        path: ["lodgeStays", ordered[index].index, "startDate"],
        message: "Lodge stays must not overlap",
      })
    }
  }

  return issues
}
